import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import TestimonialCard from './TestimonialCard';
import testimonialsData from '../../data/testimonials.json';

const TestimonialsCarousel = ({
  leadTemperature = null,
  maxItems = 3,
  compact = false,
  autoplay = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Filter testimonials by lead temperature if specified
  const filteredTestimonials = leadTemperature 
    ? testimonialsData.testimonials.filter(t => t.leadTemperature === leadTemperature)
    : testimonialsData.testimonials.filter(t => t.featured);

  const testimonials = filteredTestimonials.slice(0, maxItems);

  // Auto-play functionality
  useEffect(() => {
    if (autoplay && testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [autoplay, testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const getTemperatureTitle = (temperature) => {
    switch (temperature) {
      case 'hot': return 'Mujeres como tú que optimizaron su rendimiento';
      case 'warm': return 'Mujeres que superaron la mediocridad';
      case 'cold': return 'Mujeres que transformaron completamente sus vidas';
      default: return 'Historias reales de transformación';
    }
  };

  if (testimonials.length === 0) {
    return (
      <div className="text-center py-8">
        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="font-body text-muted-foreground">
          No hay testimonios disponibles para este perfil.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2 pt-8">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
          {leadTemperature ? getTemperatureTitle(leadTemperature) : 'Testimonios Reales'}
        </h2>
      </div>

      {/* Carousel */}
      <div className="relative">
        {compact ? (
          // Compact grid view
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial) => (
              <TestimonialCard
                key={testimonial.id}
                testimonial={testimonial}
                compact={true}
              />
            ))}
          </div>
        ) : testimonials.length === 1 ? (
          // Single testimonial
          <div className="max-w-2xl mx-auto">
            <TestimonialCard testimonial={testimonials[0]} />
          </div>
        ) : (
          // Carousel view
          <div className="relative">
            <div className="overflow-hidden pb-2">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="w-full flex-shrink-0 px-2 pb-1">
                    <div className="max-w-2xl mx-auto">
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation arrows */}
            {testimonials.length > 1 && (
              <>
                <button
                  onClick={prevTestimonial}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-smooth shadow-lg"
                  aria-label="Testimonio anterior"
                >
                  <Icon name="ChevronLeft" size={20} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-background transition-smooth shadow-lg"
                  aria-label="Siguiente testimonio"
                >
                  <Icon name="ChevronRight" size={20} />
                </button>
              </>
            )}

            {/* Indicators */}
            {testimonials.length > 1 && (
              <div className="flex justify-center space-x-2 mt-4">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-smooth ${
                      index === currentIndex 
                        ? 'bg-primary' 
                        : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                    }`}
                    aria-label={`Ir al testimonio ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instagram Reel Integration */}
      {testimonialsData.socialMedia?.instagramReels?.length > 0 && (
        <div className="text-center pt-8 mt-8 border-t-2 border-border">
          <p className="font-body text-sm text-muted-foreground mb-3">
            También puedes ver más transformaciones en mi Instagram
          </p>
          <Button
            variant="outline"
            iconName="Instagram"
            iconPosition="left"
            onClick={() => window.open(testimonialsData.socialMedia.instagramReels[0].url, '_blank')}
          >
            Ver Reel de Transformaciones
          </Button>
        </div>
      )}
    </div>
  );
};

export default TestimonialsCarousel;